import type { XOR } from 'ts-xor';
import type { EmptyRecord } from 'types/utils';
import type { Claim } from './claim';
import type { ExpiredProductsRequest } from './expired_products_requests';
import type { ResourceType } from './role';

export type MessageResource = Claim | ExpiredProductsRequest;
// TODO: handle `trades_instance`
export type MessageResourceType = 'Invoice' | 'ExpiredProductsRequest';
export enum MessageResourceEnum {
  CLAIM = 'Invoice',
  EXPIRED_PRODUCTS_REQUEST = 'ExpiredProductsRequest',
}

export type MessageKind = 'object' | 'message' | 'notification' | 'note';

export type UserRoleType = ResourceType | 'Admin';

type MessageBase = {
  id: number;
  kind: MessageKind;
  created_at: string;
  discussion_id: number;
  user_id: number;
};

type BasicNotification =
  | 'ongoing'
  | 'awaiting_signing'
  | 'signed_by_pharmacy'
  | 'closed_by_pharmacy'
  | 'refused_by_lab'
  | 'draft'
  | 'new_lab_pharmacy_discussion'
  | 'claim_draft'
  | 'claim_published'
  | 'claim_transmitted'
  | 'claim_transferred'
  | 'claim_reopened'
  | 'claim_seen'
  | 'claim_closed_by_pharmacy'
  | 'claim_closed_by_lab'
  | 'claim_notify_lab_user_selected'
  | 'validated_by_lab';

export type NotificationParams = {
  new_user: { user_fullname: string };
  removed_user: { user_fullname: string };
  created: { user_fullname: string };
  claim_admin_mail_reminder: { email?: string };
  claim_partner_mail_sharing: { email: string };
} & Record<BasicNotification, null>;

export type Notification = keyof NotificationParams;

export type SystemMessage = {
  [K in Notification]: MessageBase & {
    kind: 'notification';
    notification: K;
    notification_params: NotificationParams[K];
  };
}[Notification];

export type AdsParams = { ads_campaign_id: number } & XOR<
  EmptyRecord,
  { cta_text: string; cta_url: string }
>;

export type TextMessage = MessageBase & {
  kind: 'message';
  content: string | null;
  user_role_type: UserRoleType;
  image_url: string | null;
  file_url: string | null;
  ads_params: AdsParams | null;
};

export type InternalMessage = MessageBase & {
  kind: 'note';
  content: string | null;
  user_role_type: UserRoleType;
  image_url: string | null;
  file_url: string | null;
};

export type ObjectMessage = MessageBase & {
  kind: 'object';
  resource_type: MessageResourceType;
  user_role_type: UserRoleType;
  resource_id: number;
};

export type Message = TextMessage | SystemMessage | InternalMessage | ObjectMessage;
